import type { GetWorkflowTemplateResponse } from "../../service/query/Types";
import { addDaysToDate, convertDateToStringYYYYMMDD, getDateFromValue, parseDateOnlyStringYYYYMMDD } from "../date";
import { getWorkflowStageKey } from "./getWorkflowStageKey";
import { normalizeStage } from "./normalizeStage";

export type WorkflowStageDateValues = Record<string, Date | undefined>;
export type WorkflowStageDateMap = Record<string, WorkflowStageDateValues>;

export interface WorkflowTemplatePhaseItem {
  keyDates: string[];
  phase: string;
  sequence: number;
}

const START_KEY_NAMES = new Set(["start", "startDate"].map(normalizeStage));
const END_KEY_NAMES = new Set(["end", "endDate"].map(normalizeStage));

export const normaliseWorkflowTemplatePhases = (
  phases: GetWorkflowTemplateResponse["phases"] | undefined
): WorkflowTemplatePhaseItem[] =>
  phases
    ?.filter((phaseItem) => phaseItem.phase.trim())
    .sort((leftItem, rightItem) => leftItem.sequence - rightItem.sequence)
    .map((phaseItem) => ({
      keyDates: (phaseItem.keyDates ?? []).filter((keyDate) => keyDate.trim()),
      phase: getWorkflowStageKey(phaseItem.phase),
      sequence: phaseItem.sequence,
    })) ?? [];

export const getInitialWorkflowStageDates = (
  orderedWorkflowPhases: WorkflowTemplatePhaseItem[],
  existingStageDates: WorkflowStageDateMap
): WorkflowStageDateMap =>
  orderedWorkflowPhases.reduce<WorkflowStageDateMap>(
    (result, phaseItem) => ({
      ...result,
      [phaseItem.phase]: phaseItem.keyDates.reduce<WorkflowStageDateValues>(
        (phaseResult, keyDate) => ({
          ...phaseResult,
          [keyDate]: existingStageDates[phaseItem.phase]?.[keyDate],
        }),
        {}
      ),
    }),
    {}
  );

export const updateWorkflowStageDates = (
  dealWorkflowStageDates: WorkflowStageDateMap,
  orderedWorkflowPhases: WorkflowTemplatePhaseItem[],
  phase: string,
  keyDate: string,
  value: Date | undefined
): WorkflowStageDateMap => {
  const nextStageDates: WorkflowStageDateMap = {
    ...dealWorkflowStageDates,
    [phase]: {
      ...dealWorkflowStageDates[phase],
      [keyDate]: value,
    },
  };

  if (!END_KEY_NAMES.has(normalizeStage(keyDate))) {
    return nextStageDates;
  }

  const currentPhaseIndex = orderedWorkflowPhases.findIndex((phaseItem) => phaseItem.phase === phase);
  const nextPhase = currentPhaseIndex >= 0 ? orderedWorkflowPhases[currentPhaseIndex + 1] : undefined;
  const nextPhaseStartKey = nextPhase?.keyDates.find((nextKeyDate) => START_KEY_NAMES.has(normalizeStage(nextKeyDate)));

  if (!nextPhase || !nextPhaseStartKey) {
    return nextStageDates;
  }

  return {
    ...nextStageDates,
    [nextPhase.phase]: {
      ...nextStageDates[nextPhase.phase],
      [nextPhaseStartKey]: value ? addDaysToDate(value, 1) : undefined,
    },
  };
};

const parseWorkflowStageDateValue = (value: unknown): Date | undefined => {
  if (typeof value === "string") {
    return parseDateOnlyStringYYYYMMDD(value) ?? getDateFromValue(value);
  }

  return getDateFromValue(value);
};

export const parseWorkflowStageDates = (
  workflowStageDates: unknown,
  workflowPhases: WorkflowTemplatePhaseItem[]
): WorkflowStageDateMap => {
  if (!workflowStageDates) {
    return {};
  }

  const parsedWorkflowStageDates =
    typeof workflowStageDates === "string"
      ? (() => {
          try {
            return JSON.parse(workflowStageDates) as Record<string, Record<string, unknown>>;
          } catch {
            return null;
          }
        })()
      : (workflowStageDates as Record<string, Record<string, unknown>>);

  if (!parsedWorkflowStageDates || typeof parsedWorkflowStageDates !== "object") {
    return {};
  }

  return workflowPhases.reduce<WorkflowStageDateMap>(
    (result, phaseItem) => ({
      ...result,
      [phaseItem.phase]: phaseItem.keyDates.reduce<WorkflowStageDateValues>(
        (phaseDateResult, keyDate) => ({
          ...phaseDateResult,
          [keyDate]: parseWorkflowStageDateValue(parsedWorkflowStageDates[phaseItem.phase]?.[keyDate]),
        }),
        {}
      ),
    }),
    {}
  );
};

export const toDateOnlyString = (value: Date | undefined): string | null =>
  convertDateToStringYYYYMMDD(getDateFromValue(value));

export const mapDealWorkflowStageDates = (
  dealWorkflowStageDates: WorkflowStageDateMap
): Record<string, Record<string, string | null> | null> => {
  let hasAssignedInProgressStage = false;

  return Object.entries(dealWorkflowStageDates).reduce<Record<string, Record<string, string | null> | null>>(
    (result, [stageKey, stageDates]) => {
      const mappedStageDates = Object.entries(stageDates).reduce<Record<string, string | null>>(
        (stageResult, [fieldKey, fieldValue]) => ({
          ...stageResult,
          [fieldKey]: toDateOnlyString(fieldValue),
        }),
        {}
      );

      const hasAnyDate = Object.values(mappedStageDates).some((value) => value !== null);

      if (!hasAnyDate) {
        return result;
      }

      const status = hasAssignedInProgressStage ? null : "In progress";
      hasAssignedInProgressStage = true;

      return {
        ...result,
        [stageKey]: {
          ...mappedStageDates,
          status,
        },
      };
    },
    {}
  );
};
